import React, { useState, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { Play, Pause, Download, Loader2 } from 'lucide-react';

// Fetches a short-lived signed URL on demand (via getMixDownloadUrl) and
// plays/downloads the mix. Never embeds a stored file URL in the page — the
// URL is only requested at the moment the user clicks play or download, and
// the server checks permissions each time.
export default function MixPlayer({ mixId }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState('');
  const audioRef = useRef(null);

  const fetchUrl = async () => {
    const res = await base44.functions.invoke('getMixDownloadUrl', { mix_id: mixId });
    if (res.status === 403 || res.data?.error) {
      throw new Error(res.data?.error || 'Not allowed');
    }
    return res.data?.signed_url;
  };

  const handlePlay = async () => {
    setError('');
    if (playing) {
      audioRef.current?.pause();
      setPlaying(false);
      return;
    }
    try {
      let u = url;
      if (!u) {
        setLoading(true);
        u = await fetchUrl();
        setUrl(u);
      }
      if (audioRef.current) {
        audioRef.current.src = u;
        await audioRef.current.play();
        setPlaying(true);
      }
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setError('');
    try {
      let u = url;
      if (!u) {
        setLoading(true);
        u = await fetchUrl();
        setUrl(u);
      }
      window.open(u, '_blank');
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-0.5">
      <audio ref={audioRef} onEnded={() => setPlaying(false)} />
      <button
        onClick={handlePlay}
        disabled={loading}
        className="p-1.5 text-neutral-500 hover:text-purple-700 disabled:opacity-50"
        title={playing ? 'Pause' : 'Play'}
      >
        {loading ? <Loader2 size={15} className="animate-spin" /> : playing ? <Pause size={15} /> : <Play size={15} />}
      </button>
      <button
        onClick={handleDownload}
        disabled={loading}
        className="p-1.5 text-neutral-500 hover:text-purple-700 disabled:opacity-50"
        title="Download"
      >
        <Download size={15} />
      </button>
      {error && <span className="text-xs text-red-500 ml-1">{error}</span>}
    </div>
  );
}